/**
 * Wind zone: continuous force applied to every ragdoll body inside the area.
 * Sensor body; the GameScene checks overlap each frame.
 *
 * Visualisation is fully static (no animation — animated streamlines were
 * causing motion sickness). Reads like an aviation/map wind symbol:
 *   - A few static chevron arrows tiled across the zone, all pointing
 *     downwind. The shape itself is the directional cue, no drift needed.
 *   - A central wind-barb badge: a small disk with a shaft pointing
 *     downwind (with an arrowhead) and feather barbs on the upwind side
 *     encoding magnitude. Same convention pilots read on METAR charts.
 */

import Matter from 'matter-js';
import { Container, Graphics } from 'pixi.js';

export interface WindOptions {
  x: number;
  y: number;
  w: number;
  h: number;
  fx?: number;
  fy?: number;
}

export class Wind {
  readonly body: Matter.Body;
  readonly view: Container;
  readonly bounds: { minX: number; maxX: number; minY: number; maxY: number };
  readonly fx: number;
  readonly fy: number;

  constructor(opts: WindOptions) {
    this.fx = opts.fx ?? 0.0015;
    this.fy = opts.fy ?? 0;
    const mag = Math.hypot(this.fx, this.fy);
    // atan2 returns 0 if both components are 0; treat that as "blowing right"
    // so the visual stays defined even for a degenerate zone.
    const ang = mag > 0 ? Math.atan2(this.fy, this.fx) : 0;

    this.body = Matter.Bodies.rectangle(
      opts.x + opts.w / 2,
      opts.y + opts.h / 2,
      opts.w,
      opts.h,
      { isStatic: true, isSensor: true, label: 'wind' },
    );
    this.bounds = {
      minX: opts.x, maxX: opts.x + opts.w,
      minY: opts.y, maxY: opts.y + opts.h,
    };

    const root = new Container();

    // Cool-blue zone fill — reads as "air/sky" and contrasts with warm
    // anchors/boosters without competing for attention.
    const bg = new Graphics();
    bg.rect(opts.x, opts.y, opts.w, opts.h)
      .fill({ color: 0x66aaff, alpha: 0.06 })
      .stroke({ color: 0x88bbff, width: 1, alpha: 0.35 });
    root.addChild(bg);

    // Static chevron lattice — all chevrons point downwind. No animation.
    const flow = new Graphics();
    this.drawChevrons(flow, opts, ang, mag);
    root.addChild(flow);

    // Centre wind-barb badge. Sized to the zone so tiny zones still read,
    // capped so huge zones don't shout.
    const badge = new Graphics();
    const badgeR = Math.max(14, Math.min(26, Math.min(opts.w, opts.h) * 0.18));
    this.drawBadge(badge, opts.x + opts.w / 2, opts.y + opts.h / 2, badgeR, ang, mag);
    root.addChild(badge);

    this.view = root;
  }

  /** Tile the zone with chevrons pointing along the flow. Static — the
   *  chevron shape itself is the directional cue. Lattice is rotated to
   *  the flow axis and clipped to the zone AABB. */
  private drawChevrons(g: Graphics, o: WindOptions, ang: number, mag: number): void {
    if (mag <= 0) return;

    const ux = Math.cos(ang), uy = Math.sin(ang);   // along flow
    const px = -uy, py = ux;                        // across flow
    const cx = o.x + o.w / 2, cy = o.y + o.h / 2;

    // Project the zone corners onto the (along, across) axes so we know
    // exactly which lattice cells to iterate.
    const corners: ReadonlyArray<readonly [number, number]> = [
      [o.x, o.y], [o.x + o.w, o.y],
      [o.x, o.y + o.h], [o.x + o.w, o.y + o.h],
    ];
    let uMin = Infinity, uMax = -Infinity, pMin = Infinity, pMax = -Infinity;
    for (const [x, y] of corners) {
      const u = (x - cx) * ux + (y - cy) * uy;
      const p = (x - cx) * px + (y - cy) * py;
      if (u < uMin) uMin = u; if (u > uMax) uMax = u;
      if (p < pMin) pMin = p; if (p > pMax) pMax = p;
    }

    const spaceU = 56;        // along-flow spacing — sparse on purpose
    const spaceP = 36;        // across-flow spacing
    const chev = 6;           // half-length of each chevron leg
    const color = 0xddeeff;
    const inZone = (x: number, y: number): boolean =>
      x >= o.x && x <= o.x + o.w && y >= o.y && y <= o.y + o.h;

    const rowStart = Math.ceil(pMin / spaceP);
    const rowEnd = Math.floor(pMax / spaceP);
    for (let row = rowStart; row <= rowEnd; row++) {
      const p = row * spaceP;
      // Stagger alternate rows by half a step so chevrons interleave
      // instead of marching in a square grid.
      const stagger = (row & 1) ? spaceU / 2 : 0;
      const u0 = Math.ceil((uMin - spaceU) / spaceU) * spaceU + stagger;
      for (let u = u0; u <= uMax + spaceU; u += spaceU) {
        const tipX = cx + ux * u + px * p;
        const tipY = cy + uy * u + py * p;
        if (!inZone(tipX, tipY)) continue;
        // Chevron: two short lines forming a > pointing along +u (downwind).
        const baseU = u - chev;
        const aX = cx + ux * baseU + px * (p + chev * 0.7);
        const aY = cy + uy * baseU + py * (p + chev * 0.7);
        const bX = cx + ux * baseU + px * (p - chev * 0.7);
        const bY = cy + uy * baseU + py * (p - chev * 0.7);
        g.moveTo(aX, aY).lineTo(tipX, tipY).lineTo(bX, bY)
          .stroke({ color, width: 1.5, alpha: 0.55 });
      }
    }
  }

  /** Compass disk with a wind-barb shaft + arrowhead + speed feathers.
   *  Conventions:
   *    - Shaft points *downwind* with an arrowhead at its tip, reinforcing
   *      the chevrons' direction.
   *    - A small "N" tick on the upper edge of the disk gives a fixed
   *      orientation reference (always up = up-screen), so the player can
   *      compare the shaft angle to a known axis.
   *    - Feathers on the upwind half of the shaft encode speed (each full
   *      barb ~= 0.0005 force unit; half barb ~= 0.00025). Same shape
   *      pilots read on aviation charts. */
  private drawBadge(g: Graphics, cx: number, cy: number, r: number, ang: number, mag: number): void {
    const ux = Math.cos(ang), uy = Math.sin(ang);

    g.circle(cx, cy, r).fill({ color: 0x0a1830, alpha: 0.55 })
      .stroke({ color: 0x88bbff, width: 1, alpha: 0.7 });
    // North tick — small, on the outside of the disk pointing up.
    g.moveTo(cx, cy - r - 4).lineTo(cx, cy - r - 1)
      .stroke({ color: 0x88bbff, width: 1, alpha: 0.8 });

    if (mag <= 0) return;

    // Shaft from disk centre to the downwind edge.
    const len = r * 0.9;
    const tipX = cx + ux * len, tipY = cy + uy * len;
    g.moveTo(cx, cy).lineTo(tipX, tipY)
      .stroke({ color: 0xddeeff, width: 2, alpha: 0.95 });

    // Arrowhead at the downwind tip (two legs swept back).
    const ah = 6;
    g.moveTo(tipX, tipY)
      .lineTo(tipX - Math.cos(ang - 2.6) * ah, tipY - Math.sin(ang - 2.6) * ah)
      .moveTo(tipX, tipY)
      .lineTo(tipX - Math.cos(ang + 2.6) * ah, tipY - Math.sin(ang + 2.6) * ah)
      .stroke({ color: 0xddeeff, width: 2, alpha: 0.95 });

    // Feathers on the upwind half. Slightly swept-back for the classic
    // wind-barb look. (-ux, -uy) is the upwind direction.
    const sx = -ux, sy = -uy;
    const fpx = -uy, fpy = ux;       // perpendicular (left side of shaft)
    const units = mag * 2000;        // each "unit" ~= a full barb
    const fullBarbs = Math.min(5, Math.floor(units));
    const halfBarb = (units - fullBarbs) >= 0.5;
    let off = 3;
    const drawFeather = (length: number): void => {
      const bx = cx + sx * off, by = cy + sy * off;
      const ex = bx + fpx * length + sx * (length * 0.3);
      const ey = by + fpy * length + sy * (length * 0.3);
      g.moveTo(bx, by).lineTo(ex, ey)
        .stroke({ color: 0xddeeff, width: 2, alpha: 0.9 });
    };
    for (let i = 0; i < fullBarbs; i++) { drawFeather(7); off += 3.5; }
    if (halfBarb) drawFeather(4);
  }
}
